/**
 * Controlled image-import dialog that previews a selected image, requests OCR suggestions, and lets
 * the user select suggestions and an active bundle before creating activities. Refreshes tasks
 * using a read snapshot after import; extracted suggestions require user selection.
 *
 * @module apps/web/src/components/ocr/OCRModal
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { useTasksStore, captureTaskSnapshot } from '@/stores/tasksStore';
import { taskToUI } from '@fluxability/types';
import { Upload, Camera, Loader2, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useBundlePurposeGate } from '@/components/workspace/BundlePurposeDialog';

/** Controlled lifecycle for image recognition and review before activity creation. */
interface OCRModalProps {
  /** Whether the controlled dialog is visible. */
  open: boolean;
  /** Ask the owner to close the dialog. */
  onClose: () => void;
}

/** Editable activity candidate extracted from an image; suggested schedule fields may be absent until reviewed. */
interface OCRSuggestion {
  /** Editable recognized activity title before creation. */
  content: string;
  /** Optional extracted deadline displayed for review; null when none was recognized. */
  dueDate?: string | null;
  /** Editable recognized duration estimate before creation; null when none was recognized. */
  estimatedDuration?: number | null;
  /** Editable urgency inferred from the source image; null when none was recognized. */
  priority?: string | null;
  /** Whether the reviewed suggestion is marked complete. */
  completed: boolean;
}

/**
 * Controlled image-import dialog that previews a selected image, requests OCR suggestions, and lets
 * the user select suggestions and an active bundle before creating activities. Refreshes tasks
 * using a read snapshot after import; extracted suggestions require user selection.
 */
export function OCRModal({ open, onClose }: OCRModalProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { bundles, loadWorkspace, getBundle } = useWorkspaceStore();
  const purposeGate = useBundlePurposeGate();
  const activeBundles = useMemo(
    () => bundles.filter(bundle => bundle.status === 'active'),
    [bundles]
  );

  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<OCRSuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [selectedBundleId, setSelectedBundleId] = useState('');

  useEffect(() => {
    if (open) loadWorkspace().catch(error => console.error('Failed to load workspace', error));
  }, [open, loadWorkspace]);

  useEffect(() => {
    if (!selectedBundleId && activeBundles.length === 1) {
      setSelectedBundleId(activeBundles[0]!.id);
    }
  }, [activeBundles, selectedBundleId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please select an image file.',
        variant: 'destructive',
      });
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = e => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Process OCR
    handleOCRScan(file);
  };

  const handleOCRScan = async (file: File) => {
    setProcessing(true);
    try {
      const response = await api.ocr.scan(file);

      if (response.success && response.data) {
        const tasks = response.data.extractedTasks || [];
        setSuggestions(tasks);

        if (tasks.length > 0) {
          toast({
            title: 'OCR complete',
            description: `Found ${tasks.length} activities in your image.`,
          });
          // Pre-select all suggestions
          setSelectedSuggestions(new Set(tasks.map((_, i) => i)));
        } else {
          toast({
            title: 'No activities found',
            description: 'Could not detect any activities in the image. Try a clearer photo.',
            variant: 'destructive',
          });
        }
      }
    } catch {
      toast({
        title: 'OCR failed',
        description: 'Failed to process the image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateTasks = async () => {
    if (selectedSuggestions.size === 0) {
      toast({
        title: 'No activities selected',
        description: 'Please select at least one activity to create.',
        variant: 'destructive',
      });
      return;
    }
    if (!selectedBundleId) {
      toast({
        title: 'Bundle required',
        description: 'Choose or create a bundle before creating activities.',
        variant: 'destructive',
      });
      return;
    }
    // Imported bundles must be given a purpose before they receive new activities.
    if (getBundle(selectedBundleId)?.needsPurpose) {
      purposeGate.requirePurpose(selectedBundleId, handleCreateTasks);
      return;
    }

    setUploading(true);
    try {
      const tasksToCreate = suggestions.filter((_, i) => selectedSuggestions.has(i));

      for (const suggestion of tasksToCreate) {
        const response = await api.tasks.create({
          title: suggestion.content,
          bundle_id: selectedBundleId,
          priority: suggestion.priority || 'medium',
          duration_minutes: suggestion.estimatedDuration || 60,
          due_date: suggestion.dueDate,
        });

        if (response.success && response.data) {
          // Handled via App.tsx syncing or could do it here
        }
      }

      toast({
        title: 'Activities created',
        description: `Successfully created ${tasksToCreate.length} activities from your image.`,
      });

      // Reload activities from API instead of assuming shape
      const snapshot = captureTaskSnapshot();
      const tasksRes = await api.tasks.list();
      if (tasksRes.success && tasksRes.data) {
        const tasksList = Array.isArray(tasksRes.data) ? tasksRes.data : tasksRes.data?.tasks;
        if (tasksList) {
          useTasksStore.getState().setTasks(tasksList.map(taskToUI), snapshot);
        }
      }

      handleClose();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to create some activities. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const toggleSuggestion = (index: number) => {
    setSelectedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    setImagePreview(null);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setProcessing(false);
    setUploading(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Scan Handwritten Activity List
          </DialogTitle>
          <DialogDescription>
            Upload an image of your handwritten activities and we'll extract them using AI
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Area */}
          {!imagePreview && (
            <div
              className="hover:border-primary cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
              <p className="mb-2 text-lg font-medium">Click to upload an image</p>
              <p className="text-muted-foreground text-sm">Supports JPG, PNG, and HEIC images</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          )}

          {/* Image Preview */}
          {imagePreview && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={imagePreview}
                      alt="Uploaded image"
                      className="h-32 w-32 rounded-md object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    {processing ? (
                      <div className="text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Processing image with AI...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Processing complete</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="space-y-2">
              <div className="bg-muted/20 grid gap-2 rounded-md border p-3">
                <Label>Bundle</Label>
                {activeBundles.length > 0 ? (
                  <Select
                    value={selectedBundleId}
                    onValueChange={value =>
                      purposeGate.requirePurpose(value, () => setSelectedBundleId(value))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a bundle" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeBundles.map(bundle => (
                        <SelectItem key={bundle.id} value={bundle.id}>
                          {bundle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Create a bundle in Workspace before creating activities from a scan.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Detected Activities ({suggestions.length})</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (selectedSuggestions.size === suggestions.length) {
                      setSelectedSuggestions(new Set());
                    } else {
                      setSelectedSuggestions(new Set(suggestions.map((_, i) => i)));
                    }
                  }}
                >
                  {selectedSuggestions.size === suggestions.length ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
              <div className="max-h-[300px] space-y-2 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <Card
                    key={index}
                    className={`cursor-pointer transition-all ${
                      selectedSuggestions.has(index) ? 'ring-primary ring-2' : ''
                    }`}
                    onClick={() => toggleSuggestion(index)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{suggestion.content}</h4>
                          </div>
                          <div className="text-muted-foreground mt-2 flex flex-wrap gap-2 text-sm">
                            {suggestion.estimatedDuration && (
                              <span>{suggestion.estimatedDuration} min</span>
                            )}
                            {suggestion.priority && (
                              <Badge variant="outline">{suggestion.priority}</Badge>
                            )}
                            {suggestion.dueDate && (
                              <span>Due: {new Date(suggestion.dueDate).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        {selectedSuggestions.has(index) && (
                          <CheckCircle2 className="text-primary h-5 w-5 flex-shrink-0" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {suggestions.length > 0 && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTasks}
                disabled={uploading || selectedSuggestions.size === 0 || !selectedBundleId}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  `Create ${selectedSuggestions.size} Activities`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      {purposeGate.dialog}
    </Dialog>
  );
}
